/**
 * OPUS-1505 — live rows for the OTHER fixtures in this client's season.fixture
 * window.
 *
 * season.fixture is a ±1-week window, so it carries ~20-30 fixtures, not just
 * the one on screen. Scores for those siblings DO arrive — DDS publishes
 * fixture_result/fixture for the whole season set on
 * seasonFixtureResultsUpdated — but nothing ever applied them: the
 * orchestrator's staticDataUpdated subscription is filtered server-side to OUR
 * fixture_id (subscription.resolvers.js: `key.fixture_id === fixture_id` is the
 * only delivery route for fixture_result, and the derived-relation fallback can
 * never match that collection), so applyStaticDataPatch never sees them. They
 * stayed at whatever the initial fetch returned until a refetch — which is why
 * refreshing appeared to "fix" it, and why it looked like it only happened on
 * "some laptops": it happens to whoever is on a different fixture.
 *
 * WHY THIS IS NOT applyStaticDataPatch.
 * patchSeasonFixtureResult writes the flat root.fixture_result[0]
 * UNCONDITIONALLY, before it checks the fixture id:
 *
 *     root.fixture_result[0] = Object.assign({}, root.fixture_result[0] ?? {}, data);
 *     if (!fixtureId) return;
 *
 * So handing it another fixture's payload would paste that fixture's score onto
 * the on-air header. That is worse than the bug it would fix. These functions
 * touch season[].fixture[] and nothing else.
 *
 * Kept pure and dependency-free so it is unit-testable without mounting the
 * hook.
 */

const isPlainObject = (v) => !!v && typeof v === 'object' && !Array.isArray(v);

/** The two collections seasonFixtureResultsUpdated carries besides notices. */
export const SIBLING_ROW_COLLECTIONS = Object.freeze(['fixture_result', 'fixture']);

/**
 * Record one live sibling row into `rows` (a Map keyed by fixture_id).
 *
 * Returns true only when something was stored, so the caller can skip the
 * setState entirely for the fixtures it does not hold.
 */
export function recordSiblingRow(rows, payload, ownFixtureId) {
  if (!rows || !isPlainObject(payload)) return false;

  const { collection } = payload;
  if (!SIBLING_ROW_COLLECTIONS.includes(collection)) return false;

  const fid = payload.key?.fixture_id;
  if (!fid) return false;
  // Our own fixture belongs to the orchestrator. Applying it here as well would
  // double-write and fight the local optimistic score guard.
  if (fid === ownFixtureId) return false;

  // A delete would have to CLEAR fields rather than merge them, and the payload
  // carries no values to clear from. Leaving the row as it stands until the
  // next refetch is the honest outcome.
  if (String(payload.op ?? '').toLowerCase() === 'delete') return false;

  const values = payload.data;
  if (!isPlainObject(values)) return false;

  // The payload is SPARSE — one field, only the side that scored — so this
  // merges onto whatever is already held. Replacing would clear the other
  // team's score on every goal.
  const prev = rows.get(fid) ?? {};
  rows.set(
    fid,
    collection === 'fixture_result'
      ? { ...prev, fixture_result: { ...(prev.fixture_result ?? {}), ...values } }
      : { ...prev, fixture: { ...(prev.fixture ?? {}), ...values } },
  );
  return true;
}

/**
 * Merge every recorded sibling row into data.season[].fixture[].
 *
 * Returns `data` BY IDENTITY when nothing matched, so a goal on a fixture
 * outside this client's window costs zero renders.
 */
export function applySiblingRows(data, rows) {
  if (!rows || !rows.size || !Array.isArray(data?.season)) return data;

  let touched = false;
  const nextSeasons = data.season.map((season) => {
    const fixtures = season?.fixture;
    if (!Array.isArray(fixtures) || !fixtures.length) return season;

    let seasonTouched = false;
    const nextFixtures = fixtures.map((fx) => {
      const row = fx?.fixture_id ? rows.get(fx.fixture_id) : null;
      if (!row) return fx;
      seasonTouched = true;

      const merged = { ...fx, ...(row.fixture || {}) };
      if (row.fixture_result) {
        // fixture_result appears as both an object and a single-element array
        // depending on the read; preserve whichever shape is present.
        const existing = merged.fixture_result;
        const baseObj = Array.isArray(existing) ? existing[0] : existing;
        const base = isPlainObject(baseObj) ? baseObj : {};
        const nextFr = { ...base, ...row.fixture_result };
        merged.fixture_result = Array.isArray(existing)
          ? [nextFr, ...existing.slice(1)]
          : nextFr;
      }
      return merged;
    });

    if (!seasonTouched) return season;
    touched = true;
    return { ...season, fixture: nextFixtures };
  });

  return touched ? { ...data, season: nextSeasons } : data;
}
